import { Component, input, signal, computed, OnDestroy, inject } from '@angular/core';
import { StatusSidebar } from '@app/shared/components/status-sidebar/status-sidebar';
import { ProjectsService, TaskService } from '../../projects.service';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { Subject, takeUntil, filter } from 'rxjs';

@Component({
  selector: 'app-task-status',
  standalone: true,
  imports: [StatusSidebar],
  templateUrl: './task-status.html',
  styleUrl: './task-status.scss',
})
export class TaskStatus implements OnDestroy {
  taskId = input<string | null>(null);
  refreshTrigger = input<number>(0);
  
  // Internal refresh trigger signal for cross-tab sync
  private internalRefreshTrigger = signal<number>(0);
  private readonly destroy$ = new Subject<void>();
  private readonly crossTabSyncService = inject(CrossTabSyncService);
  
  // Computed signal that combines input and internal refresh triggers
  effectiveRefreshTrigger = computed(() => {
    return this.refreshTrigger() + this.internalRefreshTrigger();
  });

  // Configuration for issue status
  taskConfig = {
    type: 'task' as const,
    title: 'Manage Task',
    statusLabel: 'Status',
    assignLabel: 'Assigned To',
    plannedHoursLabel: 'Planned Hours',
    dueDateLabel: 'Due on',
    refNumberLabel: 'Ref No.',
    showPlannedHours: true,
    showDueDate: true,
    showRefNumber: true,
    showApprovals: true,
    showTimeSummary: true,
    customFields: [],
  };

  constructor(
    public taskService: TaskService,
    public projectsService: ProjectsService
  ) {
    this.setupCrossTabSync();
  }

  /**
   * Setup cross-tab synchronization for task status updates
   */
  private setupCrossTabSync(): void {
    this.crossTabSyncService.getSyncEvents()
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is any => !!event && !!event.type)
      )
      .subscribe((event) => {
        const currentTaskId = this.taskId();
        if (!currentTaskId) return;

        // Check if this event is for the current task
        const eventTaskId = event.taskId || event.data?.taskId;
        
        // Listen for both status updates and field updates (assignees, dates, hours, etc.)
        if (eventTaskId === currentTaskId && 
            (event.type === 'task_status_updated' || event.type === 'task_field_updated')) {
          console.log('Task-status: Received cross-tab event:', event.type, event);
          // Increment refresh trigger to force status-sidebar to refresh
          this.internalRefreshTrigger.update(value => value + 1);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
